import React, { FormEvent, useState, useEffect } from "react";
import { AlertTriangle, Image, FileText, MapPin, Plus, X, XCircle } from "lucide-react";
import { ValidationErrors } from "src/errors";
import {
  EditClaimFormDataKey,
  EditClaimFormData,
  EditClaimFormAction,
  ClaimType,
  ClaimUrgencyLevel,
  Claim,
  DAMAGE_CLAIM_TYPES,
  DISCREPANCY_CLAIM_TYPES,
  CLAIM_URGENCY_LEVELS,
  ClaimProductInputField
} from "src/types";
import { useScrollOnMessage } from "src/hooks";
import {
  TextInputBox,
  TextInputBoxLarge,
  SelectInputBox,
  MultiFileUpload,
  ExistingImageDisplay
} from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { ClaimProductInputTable, EditClaimNonOrderProductInputTable } from "src/components/tables";
import { EditClaimMapper } from "src/mappers";
import { CollapsibleSection } from "src/components/ui/collapsible";
import { ClaimNonOrderProductInputField } from "src/types";

interface EditClaimFormProps {
  formData: EditClaimFormData;
  claim: Claim;
  validationErrors?: ValidationErrors<EditClaimFormDataKey>;
  formLvlErrorMessage: string;
  isFormUpdated: boolean;
  handleInputChange: (field: EditClaimFormDataKey, value: EditClaimFormData[keyof EditClaimFormData]) => void;
  handleSubmit: (e: FormEvent) => Promise<void>;
  setAction: (action: EditClaimFormAction) => void;
  onDeleteExistingImage: (imageUrl: string) => void;
}

const getClaimTypes = (issueType: "damage" | "discrepancy"): ClaimType[] => {
  return issueType === "discrepancy" ? [...DISCREPANCY_CLAIM_TYPES] : [...DAMAGE_CLAIM_TYPES];
};

const isWrongProductClaimType = (claimType: ClaimType): boolean => {
  return claimType === "Wrong Product";
};

const EditClaimForm: React.FC<EditClaimFormProps> = ({
  formData,
  claim,
  validationErrors,
  formLvlErrorMessage,
  isFormUpdated,
  handleInputChange,
  handleSubmit,
  setAction,
  onDeleteExistingImage
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const [claimDetailStates, setClaimDetailStates] = useState<Map<string, boolean>>(new Map());

  // Build a map of product details from claim's non-order products for display
  // Also include products from formData that may have been enriched
  const productDetailsMap = React.useMemo(() => {
    const map = new Map<string, { productName: string; sku: string; barcode?: string; unitPrice: number }>();

    // First, add products from the original claim
    claim.claimDetails?.forEach((detail) => {
      detail.nonOrderProducts?.forEach((product) => {
        map.set(product.productId, {
          productName: product.productName,
          sku: product.sku,
          barcode: product.barcode,
          unitPrice: product.unitPrice
        });
      });
    });

    // Then, add products from formData (in case they were updated/enriched)
    // Note: formData doesn't have enriched product details, so this is mainly for future-proofing
    // The main source is the claim object

    return map;
  }, [claim]);

  useEffect(() => {
    setClaimDetailStates((prev) => {
      const newStates = new Map(prev);
      formData.claimDetails.forEach((detail) => {
        if (!newStates.has(detail.fieldId)) {
          newStates.set(detail.fieldId, false);
        }
      });
      const currentFieldIds = new Set(formData.claimDetails.map((d) => d.fieldId));
      newStates.forEach((_, fieldId) => {
        if (!currentFieldIds.has(fieldId)) {
          newStates.delete(fieldId);
        }
      });
      return newStates;
    });
  }, [formData.claimDetails]);

  const handleClaimDetailToggle = (fieldId: string, isOpen: boolean) => {
    setClaimDetailStates((prev) => {
      const next = new Map(prev);
      next.set(fieldId, isOpen);
      return next;
    });
  };

  const getClaimDetailState = (fieldId: string): boolean => {
    return claimDetailStates.get(fieldId) ?? false;
  };

  const handleClaimDetailChange = (
    fieldId: string,
    field: keyof ClaimNonOrderProductInputField | "nonOrderProducts",
    value: any
  ) => {
    handleInputChange(
      "claimDetails",
      formData.claimDetails.map((d) => (d.fieldId === fieldId ? { ...d, [field]: value } : d))
    );
  };

  const getNonOrderProductError = (
    detailIndex: number,
    productIndex: number,
    fieldName: "productId" | "claimQuantity" | "extraInfo"
  ): string | undefined => {
    const errorMessage = validationErrors?.getErrorMessageByFormDataKey("claimDetails");
    if (!errorMessage) return undefined;

    const searchPattern = `Claim detail ${detailIndex + 1}, Non-order product ${productIndex + 1}`;
    if (errorMessage.includes(searchPattern)) {
      if (fieldName === "productId" && errorMessage.includes("Product ID")) {
        return errorMessage.split(searchPattern)[1]?.split(":")[1]?.trim();
      }
      if (fieldName === "claimQuantity" && errorMessage.includes("Claim quantity")) {
        return errorMessage.split(searchPattern)[1]?.split(":")[1]?.trim();
      }
      if (fieldName === "extraInfo" && errorMessage.includes("Extra info")) {
        return errorMessage.split(searchPattern)[1]?.split(":")[1]?.trim();
      }
    }
    return undefined;
  };

  const getNonOrderProductsGeneralError = (detailIndex: number): string | undefined => {
    const errorMessage = validationErrors?.getErrorMessageByFormDataKey("claimDetails");
    if (!errorMessage) return undefined;

    if (
      errorMessage.includes(`Claim detail ${detailIndex + 1}: Non-order products are required`) ||
      errorMessage.includes(`Claim detail ${detailIndex + 1}: At least one valid non-order product is required`)
    ) {
      return errorMessage;
    }
    return undefined;
  };

  const getClaimDetailError = (detailIndex: number, fieldName?: string): string | undefined => {
    const errorMessage = validationErrors?.getErrorMessageByFormDataKey("claimDetails");
    if (!errorMessage) return undefined;

    if (errorMessage.includes("At least one claim detail with description is required")) {
      if (fieldName === "detailedDescription" && detailIndex === 0) {
        return errorMessage;
      }
      return undefined;
    }

    const detailMatch = errorMessage.match(/Claim detail (\d+):/i);
    if (!detailMatch) {
      return undefined;
    }

    const errorDetailIndex = parseInt(detailMatch[1], 10) - 1;
    if (detailIndex !== errorDetailIndex) {
      return undefined;
    }

    if (fieldName) {
      const fieldPatterns: Record<string, RegExp> = {
        claimType: /claim type/i,
        urgencyLevel: /urgency level/i,
        detailedDescription: /detailed description|description/i
      };
      const fieldPattern = fieldPatterns[fieldName];
      if (fieldPattern && !fieldPattern.test(errorMessage)) return undefined;
    }

    return errorMessage.replace(/^Claim detail \d+: /i, "");
  };

  const [convertedProducts, setConvertedProducts] = useState<ClaimProductInputField[]>(() =>
    formData.products.map((product, index) => ({
      fieldId: `product-${index}-${product.productId}`,
      productId: product.productId,
      productName: product.productName,
      sku: product.sku,
      barcode: product.barcode || "",
      quantity: product.quantity || 0,
      claimQuantity: product.claimQuantity.toString(),
      unitPrice: product.unitPrice
    }))
  );

  const [isInitialMount, setIsInitialMount] = useState(true);

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }

    if (formData.products.length > convertedProducts.filter((p) => !p.deleted).length) {
      setConvertedProducts((prev) => {
        const deletedMap = new Map(prev.filter((p) => p.deleted).map((p) => [p.productId, true]));
        return formData.products.map((product, index) => {
          const existing = prev.find((p) => p.productId === product.productId);
          return {
            fieldId: existing?.fieldId || `product-${index}-${product.productId}`,
            productId: product.productId,
            productName: product.productName,
            sku: product.sku,
            barcode: product.barcode || "",
            quantity: product.quantity || 0,
            claimQuantity: product.claimQuantity.toString(),
            unitPrice: product.unitPrice,
            deleted: deletedMap.get(product.productId) || false
          };
        });
      });
    } else {
      const currentNonDeletedIds = convertedProducts
        .filter((p) => !p.deleted)
        .map((p) => p.productId)
        .join(",");
      const formProductIds = formData.products.map((p) => p.productId).join(",");

      if (currentNonDeletedIds === formProductIds) {
        setConvertedProducts((prev) =>
          prev.map((prevProduct) => {
            if (prevProduct.deleted) {
              return prevProduct;
            }
            const formProduct = formData.products.find((p) => p.productId === prevProduct.productId);
            if (formProduct) {
              return {
                ...prevProduct,
                claimQuantity: formProduct.claimQuantity.toString(),
                quantity: formProduct.quantity || 0,
                originalClaimQuantity: prevProduct.originalClaimQuantity
              };
            }
            return prevProduct;
          })
        );
      } else {
        setConvertedProducts((prev) => {
          const deletedMap = new Map(prev.filter((p) => p.deleted).map((p) => [p.productId, true]));
          const originalClaimQuantityMap = new Map(
            prev.filter((p) => p.originalClaimQuantity).map((p) => [p.productId, p.originalClaimQuantity!])
          );
          return formData.products.map((product, index) => {
            const existing = prev.find((p) => p.productId === product.productId);
            return {
              fieldId: existing?.fieldId || `product-${index}-${product.productId}`,
              productId: product.productId,
              productName: product.productName,
              sku: product.sku,
              barcode: product.barcode || "",
              quantity: product.quantity || 0,
              claimQuantity: product.claimQuantity.toString(),
              unitPrice: product.unitPrice,
              deleted: deletedMap.get(product.productId) || false,
              originalClaimQuantity: originalClaimQuantityMap.get(product.productId)
            };
          });
        });
      }
    }
  }, [formData.products, isInitialMount, convertedProducts]);

  const handleSetProducts: React.Dispatch<React.SetStateAction<ClaimProductInputField[]>> = (updatedProducts) => {
    const newProducts = typeof updatedProducts === "function" ? updatedProducts(convertedProducts) : updatedProducts;

    setConvertedProducts(newProducts);

    const convertedBack = newProducts
      .filter((product) => !product.deleted)
      .map((product) => ({
        productId: product.productId,
        productName: product.productName,
        sku: product.sku,
        quantity: product.quantity || 0,
        claimQuantity: parseInt(product.claimQuantity, 10) || 0,
        unitPrice: product.unitPrice
      }));

    handleInputChange("products", convertedBack);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Edit Claim</h1>
            <p className="text-gray-300 mt-2 text-lg">Review and update claim details</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              <div className="border border-neutral-200 rounded-2xl p-5 bg-gray-50">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  Claim Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Claim ID</label>
                    <div className="text-sm text-gray-900 bg-white px-3 py-2 rounded border border-gray-200 font-mono">
                      {claim.id}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className="text-sm text-gray-900 bg-white px-3 py-2 rounded border border-gray-200">
                      {claim.status}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
                    <div className="text-sm text-gray-900 bg-white px-3 py-2 rounded border border-gray-200">
                      {claim.vendorName}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Order ID</label>
                    <div className="text-sm text-gray-900 bg-white px-3 py-2 rounded border border-gray-200 font-mono">
                      {claim.purchaseOrderId}
                    </div>
                  </div>
                  {claim.submittedByEmail && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Submitted By</label>
                      <div className="text-sm text-gray-900 bg-white px-3 py-2 rounded border border-gray-200">
                        {claim.submittedByEmail}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Products Section */}
              <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                <div className="flex items-center gap-2 border-b border-secondary-100 px-6 py-4">
                  <FileText className="h-5 w-5 text-primary-600" />
                  <h3 className="text-base font-semibold text-secondary-900">Claim Products</h3>
                </div>
                <div className="p-6">
                  {formData.products && formData.products.length > 0 ? (
                    <ClaimProductInputTable
                      products={convertedProducts}
                      setProducts={handleSetProducts}
                      minProducts={1}
                    />
                  ) : (
                    <p className="text-sm text-secondary-600">No products listed.</p>
                  )}
                  <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("products")} />
                </div>
              </div>

              {/* Claim Information Section */}
              {formData.products.length > 0 && (
                <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                  <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Claim Information
                  </h3>
                  <div className="bg-secondary-50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                        Claim Price
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-secondary-900">
                      £
                      {(
                        formData.products.reduce((total, product) => {
                          return total + product.claimQuantity * product.unitPrice;
                        }, 0) +
                        formData.claimDetails.reduce((total, detail) => {
                          if (!detail.nonOrderProducts || detail.nonOrderProducts.length === 0) {
                            return total;
                          }
                          return (
                            total +
                            detail.nonOrderProducts.reduce((detailTotal, product) => {
                              const productDetail = productDetailsMap.get(product.productId);
                              const unitPrice = productDetail?.unitPrice || 0;
                              const claimQty = parseInt(product.claimQuantity, 10) || 0;
                              return detailTotal + claimQty * unitPrice;
                            }, 0)
                          );
                        }, 0)
                      ).toFixed(2)}
                    </p>
                  </div>
                </div>
              )}

              {/* Claim Details Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-black tracking-wide uppercase flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Claim Details
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      const newDetail = EditClaimMapper.initClaimDetailInputField(formData.issueType);
                      handleInputChange("claimDetails", [...formData.claimDetails, newDetail]);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add More Claim Detail
                  </button>
                </div>
                {formData.claimDetails.map((detail, index) => {
                  const isCollapsible = formData.claimDetails.length > 1;
                  const detailContent = (
                    <div className="p-4 bg-secondary-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <SelectInputBox
                            id={`claimType-${detail.fieldId}`}
                            label="Claim Type"
                            value={detail.claimType}
                            options={getClaimTypes(formData.issueType)}
                            onChange={(value) => {
                              const newClaimType = value as ClaimType;
                              const isWrongProduct = isWrongProductClaimType(newClaimType);

                              handleInputChange(
                                "claimDetails",
                                formData.claimDetails.map((d) =>
                                  d.fieldId === detail.fieldId
                                    ? {
                                        ...d,
                                        claimType: newClaimType,
                                        nonOrderProducts:
                                          isWrongProduct && !d.nonOrderProducts ? [] : d.nonOrderProducts
                                      }
                                    : d
                                )
                              );
                            }}
                            required
                            error={getClaimDetailError(index, "claimType")}
                          />
                        </div>
                        <div>
                          <SelectInputBox
                            id={`urgencyLevel-${detail.fieldId}`}
                            label="Urgency Level"
                            value={detail.urgencyLevel}
                            options={[...CLAIM_URGENCY_LEVELS]}
                            onChange={(value) => {
                              handleInputChange(
                                "claimDetails",
                                formData.claimDetails.map((d) =>
                                  d.fieldId === detail.fieldId ? { ...d, urgencyLevel: value as ClaimUrgencyLevel } : d
                                )
                              );
                            }}
                            required
                            error={getClaimDetailError(index, "urgencyLevel")}
                          />
                        </div>
                      </div>
                      <div>
                        <TextInputBoxLarge
                          id={`detailedDescription-${detail.fieldId}`}
                          label="Detailed Description"
                          placeholder="Detailed Description (5-2000 characters)"
                          onChange={(value) => {
                            handleInputChange(
                              "claimDetails",
                              formData.claimDetails.map((d) =>
                                d.fieldId === detail.fieldId ? { ...d, detailedDescription: value } : d
                              )
                            );
                          }}
                          value={detail.detailedDescription}
                          maxLength={2000}
                          rows={5}
                        />
                        <ValidationErrorMessage message={getClaimDetailError(index, "detailedDescription")} />
                      </div>

                      {/* Non-Order Products Section - Only shown for Wrong Product claim types */}
                      {isWrongProductClaimType(detail.claimType) && (
                        <div className="mt-4">
                          <h4 className="text-sm font-semibold text-secondary-700 mb-3 uppercase tracking-wide">
                            Non-Order Products
                          </h4>
                          <EditClaimNonOrderProductInputTable
                            nonOrderProducts={detail.nonOrderProducts || []}
                            setNonOrderProducts={(updatedProducts) => {
                              const productsArray =
                                typeof updatedProducts === "function"
                                  ? updatedProducts(detail.nonOrderProducts || [])
                                  : updatedProducts;
                              handleClaimDetailChange(detail.fieldId, "nonOrderProducts", productsArray);
                            }}
                            minNonOrderProducts={isWrongProductClaimType(detail.claimType) ? 1 : 0}
                            getProductError={(productIndex, fieldName) =>
                              getNonOrderProductError(index, productIndex, fieldName)
                            }
                            generalError={getNonOrderProductsGeneralError(index)}
                            productDetails={productDetailsMap}
                          />
                        </div>
                      )}
                    </div>
                  );

                  if (isCollapsible) {
                    return (
                      <div key={detail.fieldId} className={index < formData.claimDetails.length - 1 ? "mb-4" : ""}>
                        <CollapsibleSection
                          title={
                            <div className="flex items-center justify-between w-full min-w-0">
                              <div className="flex items-center space-x-3 min-w-0">
                                <span className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-800 rounded-full flex items-center justify-center text-sm font-semibold">
                                  {index + 1}
                                </span>
                                <span className="text-sm text-secondary-700 truncate">
                                  {detail.claimType || `Claim Detail ${index + 1}`}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                                <button
                                  type="button"
                                  className="inline-flex items-center p-1.5 text-error-600 hover:text-error-800 hover:bg-error-50 rounded-md transition-colors duration-200"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleInputChange(
                                      "claimDetails",
                                      formData.claimDetails.filter((d) => d.fieldId !== detail.fieldId)
                                    );
                                    // Remove state for deleted detail
                                    setClaimDetailStates((prev) => {
                                      const next = new Map(prev);
                                      next.delete(detail.fieldId);
                                      return next;
                                    });
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          }
                          isOpen={getClaimDetailState(detail.fieldId)}
                          onToggle={(isOpen) => handleClaimDetailToggle(detail.fieldId, isOpen)}
                          variant="minimal"
                        >
                          {detailContent}
                        </CollapsibleSection>
                      </div>
                    );
                  }

                  return (
                    <div key={detail.fieldId} className="p-4 border border-secondary-200 rounded-lg bg-secondary-50">
                      {detailContent}
                    </div>
                  );
                })}
              </div>

              {/* Warehouse Location Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <MapPin className="w-4 h-4 mr-2" />
                  Warehouse Location
                </h3>
                <TextInputBox
                  id="warehouseLocation"
                  placeholder="Warehouse Location"
                  onChange={(value) => handleInputChange("warehouseLocation", value)}
                  value={formData.warehouseLocation}
                  required
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("warehouseLocation")} />
              </div>

              {/* Images Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Image className="w-4 h-4 mr-2" />
                  Claim Images
                </h3>

                {/* Existing Images */}
                {formData.existingImageUrls.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Existing Images</p>
                    <ExistingImageDisplay
                      imageUrls={formData.existingImageUrls.filter((url) => !formData.deletedImageUrls.includes(url))}
                      onDelete={onDeleteExistingImage}
                    />
                  </div>
                )}

                {/* Upload New Images */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Upload New Images</p>
                  <MultiFileUpload
                    uploadText="Upload Images (Optional)"
                    uploadedFiles={formData.claimImages}
                    onFileChange={(files) => handleInputChange("claimImages", files ?? [])}
                    fileFormat="image/jpeg, image/png, image/gif, image/webp"
                  />
                </div>
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("claimImages")} />
              </div>

              {/* Form Level Error */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Action Buttons */}
              <div className="flex items-center gap-4">
                <button
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                  onClick={() => setAction("SAVE")}
                  type="submit"
                  disabled={!isFormUpdated}
                >
                  Save
                </button>
                <button
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  onClick={() => setAction("APPROVE")}
                  type="submit"
                >
                  {isFormUpdated ? "Save and Approve" : "Approve"}
                </button>
              </div>

              {/* Rejection Section */}
              <div className="border border-error-200 rounded-2xl p-5 bg-error-50">
                <h3 className="text-sm font-semibold text-error-800 mb-4 tracking-wide uppercase flex items-center">
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject Claim
                </h3>
                <TextInputBoxLarge
                  id="rejection-reason"
                  placeholder="Rejection Reason"
                  onChange={(value) => handleInputChange("rejectionReason", value)}
                  value={formData.rejectionReason ?? ""}
                  maxLength={650}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("rejectionReason")} />
                <div className="mt-4">
                  <button
                    className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                    onClick={() => setAction("REJECT")}
                    type="submit"
                    disabled={!formData.rejectionReason || formData.rejectionReason.trim().length === 0}
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { EditClaimForm };
